import type { KnownChat } from '@/components/KnownChatsModal';
import {
  toApiRecipientVisibility,
  type ApiRecipientVisibility,
  type UiRecipientVisibility,
} from '@/utils/recipientVisibility';

export type TelegramDeliveryProvider = 'auto' | 'bot' | 'telethon';
export type RecipientSource = 'known_chat' | 'manual' | 'bot' | 'telethon';

export type KnownChatCreateFields = {
  chat_id: string;
  telegram_username?: string;
  telegram_resolution_method?: string;
  telegram_resolved_chat_id?: string;
  display_name?: string;
  delivery_provider?: TelegramDeliveryProvider;
  source?: RecipientSource;
  telegram_peer_id?: number;
  telegram_access_hash?: number;
  telegram_entity_type?: string;
  telegram_entity_source?: string;
};

export function isTelethonKnownChatSource(source: string | null | undefined): boolean {
  if (!source) return false;
  return source.toLowerCase().startsWith('telethon_');
}

export function isBotKnownChatSource(source: string | null | undefined): boolean {
  if (!source) return false;
  return source.toLowerCase().startsWith('bot_');
}

export function normalizeTelegramUsername(value: string): string {
  return value.trim().replace(/^@+/, '');
}

/** Map a Known Chats row to recipient create/update fields. */
export function knownChatRecipientFields(chat: KnownChat): KnownChatCreateFields {
  const numericId = String(chat.chat_id);
  const username = chat.username ? normalizeTelegramUsername(chat.username) : null;
  const telethonSource = isTelethonKnownChatSource(chat.source);
  const botSource = isBotKnownChatSource(chat.source);

  if (telethonSource) {
    const fields: KnownChatCreateFields = {
      chat_id: username || numericId,
      telegram_username: username || undefined,
      telegram_resolution_method: chat.source ?? undefined,
      telegram_resolved_chat_id: numericId,
      display_name: chat.first_name?.trim() || undefined,
      delivery_provider: 'telethon',
      source: 'known_chat',
      telegram_peer_id: chat.chat_id,
      telegram_entity_source: chat.source ?? undefined,
    };
    if (chat.access_hash != null) {
      fields.telegram_access_hash = chat.access_hash;
      fields.telegram_entity_type = chat.entity_type ?? 'user';
    }
    return fields;
  }

  if (botSource) {
    return {
      chat_id: numericId,
      telegram_username: username || undefined,
      display_name: chat.first_name?.trim() || undefined,
      delivery_provider: 'bot',
      source: 'known_chat',
    };
  }

  return {
    chat_id: numericId,
    telegram_username: username || undefined,
    display_name: chat.first_name?.trim() || undefined,
    delivery_provider: 'telethon',
    source: 'known_chat',
  };
}

/** Build API payload for POST /recipients. */
export function buildRecipientCreatePayload(input: {
  chat_id: string;
  display_name?: string;
  is_enabled?: boolean;
  visibility?: UiRecipientVisibility | ApiRecipientVisibility;
  owner_user_id?: string;
  telegram_username?: string;
  telegram_resolution_method?: string;
  telegram_resolved_chat_id?: string;
  delivery_provider?: TelegramDeliveryProvider;
  source?: RecipientSource;
  telegram_peer_id?: number;
  telegram_access_hash?: number;
  telegram_entity_type?: string;
  telegram_entity_source?: string;
}): {
  chat_id: string;
  display_name?: string;
  channel: string;
  is_enabled: boolean;
  telegram_username?: string;
  telegram_resolution_method?: string;
  telegram_resolved_chat_id?: string;
  delivery_provider?: TelegramDeliveryProvider;
  source?: RecipientSource;
  telegram_peer_id?: number;
  telegram_access_hash?: number;
  telegram_entity_type?: string;
  telegram_entity_source?: string;
  owner_user_id?: string;
  visibility: ApiRecipientVisibility;
} {
  const trimmedChat = input.chat_id.trim();
  const normalizedUsername =
    input.telegram_username?.trim().replace(/^@+/, '') ||
    (isPlainUsernameChatId(trimmedChat) ? normalizeTelegramUsername(trimmedChat) : undefined);

  const payload: ReturnType<typeof buildRecipientCreatePayload> = {
    chat_id: trimmedChat,
    display_name: input.display_name?.trim() || undefined,
    channel: 'telegram',
    is_enabled: input.is_enabled ?? true,
    visibility: toApiRecipientVisibility(input.visibility ?? 'private'),
  };

  if (normalizedUsername) {
    payload.telegram_username = normalizedUsername;
  }
  if (input.telegram_resolution_method) {
    payload.telegram_resolution_method = input.telegram_resolution_method;
  }
  if (input.telegram_resolved_chat_id) {
    payload.telegram_resolved_chat_id = input.telegram_resolved_chat_id;
  }
  if (input.telegram_peer_id != null) {
    payload.telegram_peer_id = input.telegram_peer_id;
  }
  if (input.telegram_access_hash != null) {
    payload.telegram_access_hash = input.telegram_access_hash;
  }
  if (input.telegram_entity_type) {
    payload.telegram_entity_type = input.telegram_entity_type;
  }
  if (input.telegram_entity_source) {
    payload.telegram_entity_source = input.telegram_entity_source;
  }
  if (input.delivery_provider) {
    payload.delivery_provider = input.delivery_provider;
  } else if (!input.source || input.source === 'manual') {
    payload.delivery_provider = 'auto';
  }
  if (input.source) {
    payload.source = input.source;
  } else if (payload.delivery_provider === 'auto') {
    payload.source = 'manual';
  }
  if (input.owner_user_id) {
    payload.owner_user_id = input.owner_user_id;
  }

  return payload;
}

function isPlainUsernameChatId(value: string): boolean {
  const normalized = value.trim().replace(/^@+/, '');
  if (!normalized || normalized.toLowerCase() === 'me') return false;
  if (/^-?\d+$/.test(normalized)) return false;
  return /^[a-zA-Z][a-zA-Z0-9_]{4,31}$/.test(normalized);
}

export function knownChatCreatePayload(
  chat: KnownChat,
  options?: {
    is_enabled?: boolean;
    visibility?: UiRecipientVisibility | ApiRecipientVisibility;
    owner_user_id?: string;
  },
) {
  const fields = knownChatRecipientFields(chat);
  return buildRecipientCreatePayload({
    chat_id: fields.chat_id,
    display_name: fields.display_name,
    is_enabled: options?.is_enabled,
    visibility: options?.visibility,
    owner_user_id: options?.owner_user_id,
    telegram_username: fields.telegram_username,
    telegram_resolution_method: fields.telegram_resolution_method,
    telegram_resolved_chat_id: fields.telegram_resolved_chat_id,
    delivery_provider: fields.delivery_provider,
    source: fields.source,
    telegram_peer_id: fields.telegram_peer_id,
    telegram_access_hash: fields.telegram_access_hash,
    telegram_entity_type: fields.telegram_entity_type,
    telegram_entity_source: fields.telegram_entity_source,
  });
}

export type CreateResolutionMeta = {
  telegram_resolution_method?: string;
  telegram_resolved_chat_id?: string;
  telegram_username?: string;
  delivery_provider?: TelegramDeliveryProvider;
  source?: RecipientSource;
};

/** After Resolve ID, keep Telethon rows on username — never downgrade to numeric chat_id. */
export function resolvePreviewToCreateFields(
  input: string,
  preview: {
    resolved_chat_id?: string | null;
    resolution_method?: string | null;
    username?: string | null;
  },
): { chat_id: string; meta: CreateResolutionMeta | null } {
  const method = preview.resolution_method ?? undefined;
  const username = normalizeTelegramUsername(preview.username || input);
  const isTelethon = Boolean(method?.startsWith('telethon_'));
  const isBot = Boolean(method?.startsWith('bot_') || method === 'numeric');

  if (isTelethon && username && preview.resolved_chat_id) {
    return {
      chat_id: username,
      meta: {
        telegram_resolution_method: method,
        telegram_resolved_chat_id: preview.resolved_chat_id,
        telegram_username: username,
        delivery_provider: 'auto',
        source: 'manual',
      },
    };
  }

  if (isBot && preview.resolved_chat_id) {
    return {
      chat_id: preview.resolved_chat_id,
      meta: {
        telegram_resolution_method: method,
        telegram_resolved_chat_id: preview.resolved_chat_id,
        telegram_username: username || undefined,
        delivery_provider: 'bot',
        source: 'manual',
      },
    };
  }

  return {
    chat_id: preview.resolved_chat_id || username || input.trim(),
    meta: null,
  };
}

export const DELIVERY_PROVIDER_OPTIONS: { value: TelegramDeliveryProvider; label: string; hint: string }[] = [
  {
    value: 'auto',
    label: 'Auto: Telethon first',
    hint: 'Try Telethon when your session is authorized, then fall back to Bot API if needed.',
  },
  {
    value: 'telethon',
    label: 'Telethon session',
    hint: 'Send from the authorized Telegram session. Recipients do not need to start the bot.',
  },
  {
    value: 'bot',
    label: 'Bot API',
    hint: 'Send via the Telegram bot. Recipient must open the bot and send /start first.',
  },
];
